import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { GuestLoginRequestDto, GuestRegisterRequestDto, OAuthLoginResponseDto } from './api/guest-auth.models';
import { apiUrl } from './api-url';
import { GuestClientContextService } from './guest-client-context.service';
import { TokenStorage } from './token-storage.service';

function extractHttpError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      for (const key of ['message', 'detail', 'title', 'error']) {
        const v = o[key];
        if (typeof v === 'string' && v.trim()) return v;
      }
    }
    return err.message || `HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return 'Request failed';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorage);
  private readonly guestClient = inject(GuestClientContextService);

  readonly isAuthenticated = computed(() => this.tokenStorage.accessToken() !== null);
  readonly userEmail = this.tokenStorage.userEmail;

  private getClientId(): string | null {
    return this.guestClient.resolveClientId();
  }

  private async loginWithPassword(email: string, password: string): Promise<void> {
    const clientId = this.getClientId();
    const body: GuestLoginRequestDto = {
      email,
      password,
      ...(clientId ? { clientId } : {}),
    };
    const res = await firstValueFrom(
      this.http.post<OAuthLoginResponseDto>(apiUrl('/api/v1/public/guest-auth/login'), body),
    );
    const token = res.jwtToken;
    const mail = res.email ?? email;
    if (!token) {
      throw new Error('Login succeeded but the API did not return a JWT.');
    }
    this.tokenStorage.setSession(token, mail, res.clientId ?? clientId ?? null, {
      firstName: res.firstName,
      lastName: res.lastName,
    });
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    const clientId = this.getClientId();
    if (!clientId) {
      return {
        ok: false,
        error:
          'Marina is not selected. Open the app using your marina QR code (the link must include ?clientId=…).',
      };
    }
    const body: GuestRegisterRequestDto = {
      clientId,
      email,
      password,
      firstName,
      lastName,
    };
    try {
      await firstValueFrom(
        this.http.post(apiUrl('/api/v1/public/guest-auth/register'), body, {
          observe: 'response',
          responseType: 'text',
        }),
      );
    } catch (e) {
      return { ok: false, error: extractHttpError(e) };
    }
    try {
      await this.loginWithPassword(email, password);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: `Account created but sign-in failed: ${extractHttpError(e)}`,
      };
    }
  }

  async login(
    email: string,
    password: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      await this.loginWithPassword(email, password);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractHttpError(e) };
    }
  }

  logout(): void {
    this.tokenStorage.clear();
  }
}
